import React, { useEffect, useRef, useState } from "react"
import { Button, Carousel, Skeleton, Typography } from "antd"
import type { CarouselRef } from "antd/es/carousel"
import { swimminglyApi } from "../utils/helperFunctions"
import { faDisplay, faUpRightFromSquare, faLock } from "@fortawesome/pro-regular-svg-icons"
import { faMobileScreenButton, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { tierMeta } from "./pricingTierStyling"
import './WebsitesTemplatesTeaser.css'

type DeviceMode = "Desktop" | "Mobile"

export type TemplateRecord = {
  id: number
  slug: string
  projectId: number
  status: "draft" | "published" | "archived"
  name: string
  description?: string
  pricingTier?: "starter" | "plus"
  previewUrl?: string
  desktopMediaId?: number
  desktopMediaFilename?: string
  desktopMediaUrl?: string
  mobileMediaId?: number
  mobileMediaFilename?: string
  mobileMediaUrl?: string
}

interface WebsitesTemplatesTeaserProps {
  variant?: "full" | "embedded"
  userTier?: "starter" | "plus"
  selectedTemplateId?: number | null
  onSelectTemplate?: (template: TemplateRecord | null) => void
  onSelectionPendingChange?: (pending: boolean) => void
  forceMobileView?: boolean
  fullWidth?: boolean
}

function WebsitesTemplatesTeaser({
  variant = "full",
  userTier,
  selectedTemplateId,
  onSelectTemplate,
  onSelectionPendingChange,
  forceMobileView = false,
  fullWidth = false,
}: WebsitesTemplatesTeaserProps): JSX.Element {
  const [mode, setMode] = useState<DeviceMode>(forceMobileView ? "Mobile" : "Desktop")
  const [currentIndex, setCurrentIndex] = useState(0)
  const [templates, setTemplates] = useState<TemplateRecord[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templatesError, setTemplatesError] = useState<string | null>(null)

  const carouselRef = useRef<CarouselRef>(null)
  const pendingCarouselIndexRef = useRef<number | null>(null)
  const selectionDebounceTimerRef = useRef<number | null>(null)
  const pendingSafetyTimerRef = useRef<number | null>(null)

  const templatesRef = useRef<TemplateRecord[]>([])
  const onSelectTemplateRef = useRef<WebsitesTemplatesTeaserProps["onSelectTemplate"]>(undefined)
  const onSelectionPendingChangeRef = useRef<
    WebsitesTemplatesTeaserProps["onSelectionPendingChange"]
  >(undefined)

  const debugTemplates =
    typeof window !== "undefined" && window.localStorage?.getItem("debugWebTemplates") === "1"

  useEffect(() => {
    templatesRef.current = templates
  }, [templates])

  useEffect(() => {
    onSelectTemplateRef.current = onSelectTemplate
  }, [onSelectTemplate])

  useEffect(() => {
    onSelectionPendingChangeRef.current = onSelectionPendingChange
  }, [onSelectionPendingChange])

  useEffect(() => {
    return () => {
      if (selectionDebounceTimerRef.current) {
        window.clearTimeout(selectionDebounceTimerRef.current)
        selectionDebounceTimerRef.current = null
      }
      if (pendingSafetyTimerRef.current) {
        window.clearTimeout(pendingSafetyTimerRef.current)
        pendingSafetyTimerRef.current = null
      }
      onSelectionPendingChangeRef.current?.(false)
    }
  }, [])

  function normalizeCarouselIndex(index: number, length: number) {
    if (!Number.isFinite(index) || length <= 0) return 0
    return ((index % length) + length) % length
  }

  // Fetch templates from DB, filter and sort as requested
  useEffect(() => {
    async function fetchTemplates() {
      setTemplatesLoading(true)
      setTemplatesError(null)
      try {
        const res = await swimminglyApi.get("/api/websites/templates/public")
        let arr: TemplateRecord[] = Array.isArray(res) ? res : res?.data || []
        arr = arr
          .filter(t => t.status === "published")
          .sort((a, b) => {
            if (a.pricingTier === b.pricingTier) return 0
            if (a.pricingTier === "starter") return -1
            return 1
          })
        setTemplates(arr)

        // For embedded variant (wizard), immediately treat the first slide as selected.
        // We intentionally run this only on initial mount to avoid re-fetch loops.
        if (variant === "embedded" && arr.length > 0) {
          pendingCarouselIndexRef.current = 0
          setCurrentIndex(0)
          onSelectTemplateRef.current?.(arr[0])
          onSelectionPendingChangeRef.current?.(false)
        }
      } catch (e: any) {
        // Surface a friendly, consistent message in the UI; backend error can be logged if needed
        console.error("Failed to load website templates", e)
        setTemplatesError("Templates currently unavailable")
      } finally {
        setTemplatesLoading(false)
      }
    }
    fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleBeforeChange(_from: number, to: number) {
    const templatesLength = templatesRef.current.length
    if (variant === "embedded") {
      onSelectionPendingChangeRef.current?.(true)
      if (selectionDebounceTimerRef.current) {
        window.clearTimeout(selectionDebounceTimerRef.current)
        selectionDebounceTimerRef.current = null
      }
      if (pendingSafetyTimerRef.current) {
        window.clearTimeout(pendingSafetyTimerRef.current)
        pendingSafetyTimerRef.current = null
      }

      // Safety valve: if the carousel never emits `afterChange` (or does so
      // in a way that bypasses our embedded selection path), don't leave the
      // footer stuck in a pending/Skeleton state.
      pendingSafetyTimerRef.current = window.setTimeout(() => {
        onSelectionPendingChangeRef.current?.(false)
        pendingSafetyTimerRef.current = null
      }, 1200)
    }

    // Ant Carousel is backed by react-slick; with `infinite`, the index can
    // occasionally be outside [0..n-1] during fast interactions.
    const pendingIndex = templatesLength > 0 ? normalizeCarouselIndex(to, templatesLength) : null
    pendingCarouselIndexRef.current = pendingIndex

    // In embedded mode, drive the debounced “footer selection” off `beforeChange`
    // so it still works even when `afterChange` doesn’t fire reliably.
    if (variant === "embedded") {
      if (selectionDebounceTimerRef.current) {
        window.clearTimeout(selectionDebounceTimerRef.current)
      }

      selectionDebounceTimerRef.current = window.setTimeout(() => {
        const templatesAtCommit = templatesRef.current
        const idxCandidate = pendingCarouselIndexRef.current
        const idx =
          idxCandidate === null ? 0 : normalizeCarouselIndex(idxCandidate, templatesAtCommit.length)
        const template = templatesAtCommit[idx]
        if (template && onSelectTemplateRef.current) {
          onSelectTemplateRef.current(template)
        }

        if (pendingSafetyTimerRef.current) {
          window.clearTimeout(pendingSafetyTimerRef.current)
          pendingSafetyTimerRef.current = null
        }

        onSelectionPendingChangeRef.current?.(false)
        selectionDebounceTimerRef.current = null

        if (debugTemplates) {
          console.log("[WebsitesTemplatesTeaser] commitSelection(beforeChange)", {
            idxCandidate,
            idx,
            templatesLength: templatesAtCommit.length,
            committedTemplateId: template?.id,
            committedTemplateName: template?.name,
          })
        }
      }, 150)
    }

    if (debugTemplates) {
      console.log("[WebsitesTemplatesTeaser] beforeChange", {
        from: _from,
        to,
        templatesLength,
        pendingIndex: pendingCarouselIndexRef.current,
      })
    }
  }

  function handleAfterChange(current: number) {
    const templatesNow = templatesRef.current
    const normalized = normalizeCarouselIndex(current, templatesNow.length)
    setCurrentIndex(normalized)

    if (debugTemplates) {
      console.log("[WebsitesTemplatesTeaser] afterChange", {
        current,
        templatesLength: templatesNow.length,
        normalized,
      })
    }

    // Embedded footer selection is handled via `beforeChange` debounce.
  }

  function onPreviewLive(t: TemplateRecord) {
    if (t.previewUrl) window.open(t.previewUrl, "_blank", "noopener,noreferrer")
  }

  const handleTemplateSelect = (t: TemplateRecord) => {
    if (!onSelectTemplate) return
    if (selectedTemplateId && selectedTemplateId === t.id) {
      onSelectTemplate(null)
    } else {
      onSelectTemplate(t)
    }
  }

  function Slide({
    template,
    isLocked,
    isSelected,
    onSelect,
    forceMobile,
  }: {
    template: TemplateRecord
    isLocked: boolean
    isSelected: boolean
    onSelect?: (t: TemplateRecord) => void
    forceMobile?: boolean
  }) {
    const tm = tierMeta(template.pricingTier)
    const effectiveMode = forceMobile ? "Mobile" : mode
    const src =
      effectiveMode === "Desktop"
        ? template.desktopMediaUrl || ""
        : template.mobileMediaUrl || ""

    return (
      <div
        className="template-wrap"
        style={{
          position: "relative",
          outline: "none",
          borderRadius: 16,
          opacity: 1,
          cursor: onSelect ? "pointer" : "default",
        }}
        tabIndex={0}
        onClick={() => {
          if (isLocked) return
          if (onSelect) onSelect(template)
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            if (isLocked) return
            if (onSelect) onSelect(template)
          }
        }}
      >
        {/* --- Corner Plan Tier Badge (styling from pricingTierStyling) --- */}
        <div className="tier-badge" style={tm.tagStyle}>
          <FontAwesomeIcon icon={tm.icon} style={{ marginRight: 6 }} />
          <span>{tm.label}</span>
        </div>

        {/* --- Locked pill in upper-left of thumbnail/skin --- */}
        {isLocked && (
          <div
            className="template-lock-pill"
            style={{
              position: "absolute",
              top: 16,
              left: 16,
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.75)",
              border: "1px solid rgba(255,255,255,0.4)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 0.2,
              zIndex: 3,
              pointerEvents: "none",
            }}
          >
            <FontAwesomeIcon icon={faLock} style={{ marginRight: 6, fontSize: 12 }} />
            <span>Locked template - Plus plan required</span>
          </div>
        )}

        {/* --- Skin --- */}
        {effectiveMode === "Desktop" ? (
          <div style={desktopSkinStyle} className="template-skin">
            <div style={desktopChromeStyle}>
              <div style={chromeDotsStyle}>
                <span style={dotStyle("#ff5f56")} />
                <span style={dotStyle("#ffbd2e")} />
                <span style={dotStyle("#27c93f")} />
              </div>
            </div>
            <div style={desktopViewportStyle} className="desktop-viewport">
              {!src && <Skeleton.Image active style={{ width: "50%", height: "50%" }} />}
              {src && (
                <img
                  src={src}
                  alt={`${template.name} Desktop preview`}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }}
                />
              )}
            </div>
            {/* --- Hover darken overlay --- */}
            <div className="skin-darken" />
          </div>
        ) : (
          <div style={mobileSkinStyle} className="template-skin">
            <div style={mobileNotchStyle} />
            <div style={mobileViewportStyle} className="mobile-viewport">
              {!src && <Skeleton.Image active style={{ width: "60%", height: "60%" }} />}
              {src && (
                <img
                  src={src}
                  alt={`${template.name} Mobile preview`}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block", borderRadius: 16 }}
                />
              )}
            </div>
            {/* --- Hover darken overlay --- */}
            <div className="skin-darken" />
          </div>
        )}

        {/* --- Bottom info + toggles overlay (appears on hover/focus) --- */}
        <div className="template-info-overlay">
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <Typography.Title level={5} style={{ margin: 0, color: "#fff" }}>
                {template.name}
              </Typography.Title>
              {template.description && (
                <Typography.Text
                  style={{
                    marginTop: 2,
                    display: "block",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {template.description}
                </Typography.Text>
              )}
            </div>
            {!forceMobileView && (
              <div style={overlayToggleRowStyle}>
                <Button
                  size="small"
                  type={mode === "Desktop" ? "primary" : "default"}
                  onClick={e => {
                    e.stopPropagation()
                    setMode("Desktop")
                  }}
                  icon={<FontAwesomeIcon icon={faDisplay} />}
                />
                <Button
                  size="small"
                  type={mode === "Mobile" ? "primary" : "default"}
                  onClick={e => {
                    e.stopPropagation()
                    setMode("Mobile")
                  }}
                  icon={<FontAwesomeIcon icon={faMobileScreenButton} />}
                />
              </div>
            )}
          </div>
        </div>

        {/* --- Preview Live Button (centered, gold, 1.5x bigger, scales on hover/focus) --- */}
        <button
          className="preview-live-btn"
          onClick={e => {
            e.stopPropagation()
            onPreviewLive(template)
          }}
          type="button"
        >
          <span style={{ fontSize: "1em", display: "flex", alignItems: "center" }}>
            Preview Live
            <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginLeft: 12, fontSize: "1em" }} />
          </span>
        </button>
      </div>
    )
  }

  const currentTemplate = templates[currentIndex]

  if (templatesLoading) return <Skeleton active />
  if (templatesError) {
    return (
      <Typography.Text type="secondary">
        {templatesError}
      </Typography.Text>
    )
  }
  if (!templates.length) {
    return (
      <Typography.Text type="secondary">
        Templates currently unavailable
      </Typography.Text>
    )
  }

  const wrapperMaxWidth = variant === "embedded" ? 640 : 1200

  return (
    <>
      {variant !== "embedded" && (
        <div style={{ display: "grid", gap: 4, marginTop: 40 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Browse Templates</Typography.Title>
          <Typography.Text type="secondary">
            Website templates designed by aquatics professionals, just for your organization.
            <br /><br />
            All templates are fully customizable. Change color palettes with a click. One-page or multi-page sites.
          </Typography.Text>
        </div>
      )}

      {variant === "embedded" ? (
        <div style={{ marginTop: fullWidth ? 0 : 16 }}>
          <div
            className="templates-teaser-wrap"
            style={{ 
              width: "100%", 
              maxWidth: fullWidth ? "none" : wrapperMaxWidth, 
              margin: fullWidth ? 0 : "0 auto", 
              position: "relative" 
            }}
          >
            <Button
              type="default"
              shape="circle"
              size="large"
              onClick={() => carouselRef.current?.prev()}
              style={{
                ...overlayArrowStyle,
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
              }}
              aria-label="Previous template"
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 20 }} />
            </Button>

            <Button
              type="default"
              shape="circle"
              size="large"
              onClick={() => carouselRef.current?.next()}
              style={{
                ...overlayArrowStyle,
                position: "absolute",
                top: "50%",
                right: 12,
                transform: "translateY(-50%)",
              }}
              aria-label="Next template"
            >
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 20 }} />
            </Button>

            <Carousel
              infinite
              draggable
              dots={false}
              arrows={false}
              adaptiveHeight
              beforeChange={handleBeforeChange}
              afterChange={handleAfterChange}
              ref={carouselRef}
            >
              {templates.map((t, index) => {
                const isLocked = userTier === "starter" && t.pricingTier === "plus"
                const isSelected = index === currentIndex
                return (
                  <div key={t.id}>
                    <Slide
                      template={t}
                      isLocked={!!isLocked}
                      isSelected={!!isSelected}
                      forceMobile={forceMobileView}
                      // Selection in embedded mode is driven purely by the
                      // currently visible slide; clicking does not toggle.
                      onSelect={undefined}
                    />
                  </div>
                )
              })}
            </Carousel>
          </div>
        </div>
      ) : (
        <div>
          <div
            className="templates-teaser-wrap"
            style={{ width: "100%", maxWidth: wrapperMaxWidth, margin: "0 auto", position: "relative" }}
          >
            <Button
              type="default"
              shape="circle"
              size="large"
              onClick={() => carouselRef.current?.prev()}
              style={{
                ...overlayArrowStyle,
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
              }}
              aria-label="Previous template"
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 20 }} />
            </Button>

            <Button
              type="default"
              shape="circle"
              size="large"
              onClick={() => carouselRef.current?.next()}
              style={{
                ...overlayArrowStyle,
                position: "absolute",
                top: "50%",
                right: 12,
                transform: "translateY(-50%)",
              }}
              aria-label="Next template"
            >
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 20 }} />
            </Button>

            <Carousel
              infinite
              draggable
              dots={false}
              arrows={false}
              adaptiveHeight
              beforeChange={handleBeforeChange}
              afterChange={handleAfterChange}
              ref={carouselRef}
            >
              {templates.map((t, index) => {
                const isLocked = userTier === "starter" && t.pricingTier === "plus"
                const isSelected = !!selectedTemplateId && selectedTemplateId === t.id
                return (
                  <div key={t.id}>
                    <Slide
                      template={t}
                      isLocked={!!isLocked}
                      isSelected={!!isSelected}
                      forceMobile={forceMobileView}
                      onSelect={handleTemplateSelect}
                    />
                  </div>
                )
              })}
            </Carousel>
          </div>
        </div>
      )}
    </>
  )
}

export default React.memo(WebsitesTemplatesTeaser)

// --- Styles (unchanged from your previous implementation) ---

const desktopSkinStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 900,
  height: 550,
  background: "#f8f8f8",
  borderRadius: 16,
  boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
  margin: "0 auto",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}
const desktopChromeStyle: React.CSSProperties = {
  height: 28,
  background: "#ededed",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  display: "flex",
  alignItems: "center",
  paddingLeft: 16,
}
const chromeDotsStyle: React.CSSProperties = {
  display: "flex",
  gap: 6,
}
const dotStyle = (color: string): React.CSSProperties => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: color,
  display: "inline-block",
})
const desktopViewportStyle: React.CSSProperties = {
  flex: 1,
  background: "#fff",
  borderBottomLeftRadius: 16,
  borderBottomRightRadius: 16,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
}

const mobileSkinStyle: React.CSSProperties = {
  width: 254,
  height: 550,
  background: "linear-gradient(180deg, #000000, #1a1a1a)", // Slight gradient
  borderRadius: 32,
  boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
  margin: "0 auto",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  border: "8px solid black", // Thin black bezel
}

const mobileNotchStyle: React.CSSProperties = {
  width: "30%", // Notch spans 30% of the width
  height: 16,
  background: "#000",
  borderRadius: "0 0 8px 8px", // Rounded bottom edges
  position: "absolute",
  top: 0,
  left: "35%", // Center the notch
}

const mobileViewportStyle: React.CSSProperties = {
  flex: 1,
  background: "#000", // Black background for the viewport
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "calc(100% - 4px)", // Account for the 2px bezel on both sides
  height: "calc(100% - 4px)", // Account for the 2px bezel on top and bottom
  margin: "2px", // Center the viewport within the bezel
}

const overlayBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 24,
  width: "100%",
  gap: 12,
}
const overlayArrowStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #eee",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  zIndex: 4,
}
const overlayCenterStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
}
const overlayToggleRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 8,
  justifyContent: "center",
}